<template>
	<div :class="{'page-fixed-height': !activePanel}">
		<div class="sideways-page-one" :class="{'disabled': !activePanel}">
			<!-- BEGIN PAGE BAR -->
			<div class="page-bar">
				<breadcrumb :crumbs="breadcrumbArray"></breadcrumb>
			</div>
			<!-- END PAGE BAR -->
			<!-- BEGIN PAGE TITLE-->
			<h1 class="page-title">Locations</h1>
			<!-- END PAGE TITLE-->
			<div class="note note-info">
				Create and manage your business locations.
			</div>
			<button type="button" class="btn blue btn-lg" @click="throttledCreate()">Create a New Location</button>
			<div class="margin-top-20">
				<div>

					<!-- SEARCH PANEL START -->
					<div class="portlet box blue-hoki">
						<div class="portlet-title" @click="toggleSearchPanel()">
							<div class="caption">
								<i class="fa fa-search"></i>
								Search Panel
							</div>
							<div class="tools">
								<a :class="{'expand': !searchCollapse, 'collapse': searchCollapse}"></a>
							</div>
						</div>
						<div class="portlet-body" :class="{'display-hide': searchCollapse}">
							<form role="form" @submit.prevent="advancedLocationSearch()">
								<div class="form-body row">
									<div class="col-md-6">
										<div class="alert alert-danger" v-if="searchError.length">
											<button class="close" data-close="alert" @click="clearSearchError()"></button>
											<span>{{ searchError }}</span>
										</div>
										<div class="form-group form-md-line-input form-md-floating-label">
											<input type="text" class="form-control input-sm" :class="{'edited': advancedSearch.search.length}" id="search_options_search" v-model="advancedSearch.search" :tabindex="activePanel ? 0 : -1">
											<label for="search_options_search">Search</label>
											<span class="help-block persist" v-if="computedSearchOptions.length">Search by <span v-for="(option, index) in computedSearchOptions"><span v-if="index !== 0">, </span>{{ option.name }}</span>.</span>
											<span class="help-block persist" v-else>Select search terms below.</span>
										</div>
										<div class="md-checkbox-inline margin-top-25">
											<div class="md-checkbox" v-for="option in advancedSearch.searchOptions">
												<input type="checkbox" :id="`advanced_search_checkbox_${option.value}`" class="md-check" v-model="option.checked">
												<label :for="`advanced_search_checkbox_${option.value}`">
													<span></span>
													<span class="check"></span>
													<span class="box"></span>
													{{ option.name }}
												</label>
											</div>
										</div>
									</div>
								</div>
								<div class="form-actions right">
									<button type="button" class="btn btn-default" :tabindex="activePanel ? 0 : -1" @click="resetLocationSearch()"> Reset Search </button>
									<button type="submit" class="btn blue" :tabindex="activePanel ? 0 : -1"><i class="fa fa-search"></i> Search </button>
								</div>
							</form>
						</div>
					</div>
					<!-- SEARCH PANEL END -->

				</div>
				<div class="relative-block">

					<!-- LOCATIONS TABLE START -->
					<loading-screen :show="loadingLocationData" :color="'#3598dc'" :display="'inline'"></loading-screen>
					<div v-if="loadingLocationData === false">
						<div class="clearfix">
							<dropdown>
								<button slot="button" type="button" class="btn btn-sm btn-outline dropdown-toggle btn-default" data-toggle="dropdown">
									Sort By<span v-if="sortBy.field"><b>: {{ sortOptions[sortBy.field] }} <i class="fa fa-sort-alpha-asc" v-if="sortBy.order === 'asc'"></i><i class="fa fa-sort-alpha-desc" v-if="sortBy.order === 'desc'"></i></b></span>
									<i class="fa fa-angle-down"></i>
								</button>
								<ul slot="dropdown-menu" class="dropdown-menu">
									<li class="rounded-button-group-block">
										<div class="btn-group" data-toggle="buttons">
											<label class="btn blue btn-outline" for="sort_asc" :class="{'active': sortBy.order === 'asc'}">
												<input type="radio" class="toggle" id="sort_asc" value="asc" v-model="sortBy.order">
												<i class="fa fa-sort-alpha-asc"></i>
											</label>
											<label class="btn blue btn-outline" for="sort_desc" :class="{'active': sortBy.order === 'desc'}">
												<input type="radio" class="toggle" id="sort_desc" value="desc" v-model="sortBy.order">
												<i class="fa fa-sort-alpha-desc"></i>
											</label>
										</div>
									</li>
									<li :class="{'active': sortBy.field === 'name'}">
										<a @click="setSortField('location_name')">Location Name</a>
									</li>
								</ul>
							</dropdown>
							<page-results class="pull-right" :totalResults="totalResults" :activePage="activePage" @pageResults="pageResultsUpdate"></page-results>
						</div>
						<div class="margin-top-20">
							<pagination :passedPage="activePage" :numPages="numPages" @activePageChange="activePageUpdate"></pagination>
						</div>
						<div class="margin-top-30 row search-content-4" v-if="numPages !== 0">
							<div class="search-table table-responsive">
								<table class="table table-bordered table-striped table-condensed">
									<thead class="bg-blue">
										<tr>
											<th></th>
											<th><a class="no-click">Actions</a></th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="location in locations">
											<td>
												<h3><small v-if="location.store_number"> #{{ location.store_number }} </small> {{ location.location_name }}</h3>
												<p><b>{{ location.city }}, {{ location.country }}</b></p>
											</td>
											<td>
												<dropdown>
													<button slot="button" type="button" class="btn btn-sm btn-outline dropdown-toggle btn-default" data-toggle="dropdown">
														<i class="fa fa-cogs"></i>
													</button>
													<ul slot="dropdown-menu" class="dropdown-menu">
														<li @click="selectLocation(location)">
															<a>Select Location</a>
														</li>
														<li>
															<a @click="editLocationDetails(location.location_id)">Edit Location Details</a>
														</li>
														<li>
															<a @click="editLocationAddress(location.location_id)">Edit Location Address</a>
														</li>
														<li>
															<a @click="editLocationAppAccess(location.location_id)">View Location App Access</a>
														</li>
														<li>
															<a @click="deleteLocationConfirmation(location)">Delete Location</a>
														</li>
													</ul>
												</dropdown>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<no-results :show="numPages === 0" :type="'locations'"></no-results>
						<pagination :passedPage="activePage" :numPages="numPages" @activePageChange="activePageUpdate"></pagination>
					</div>
					<!-- LOCATIONS TABLE END -->

				</div>
			</div>
		</div>
		
		<!-- PANEL COMPONENTS START -->
	    <div class="sideways-page-two" :class="{'active': !activePanel, 'inactive': activePanel}" :style="{height: computedHeight}">
	    	<div class="sideways-blank-fold">
	    		<div class="text-center">
	    			<button type="button" class="btn blue btn-lg margin-bottom-10" @click="closePanel()">Return to this page</button>
	    		</div>
	    	</div>
	    	<create-location></create-location>
	    	<edit-location-details></edit-location-details>
	    	<edit-location-address></edit-location-address>
	    	<location-app-access></location-app-access>
	    </div>
		<!-- PANEL COMPONENTS END -->

	</div>
</template>
<script>
import $ from 'jquery'
import Breadcrumb from '../../modules/Breadcrumb'
import Dropdown from '../../modules/Dropdown'
import LoadingScreen from '../../modules/LoadingScreen'
import NoResults from '../../modules/NoResults'
import PageResults from '../../modules/PageResults'
import Pagination from '../../modules/Pagination'
import { throttle, debounce } from 'lodash'
import Location from '../../../controllers/Location'
import CreateLocation from './Locations/CreateLocation'
import EditLocationDetails from './Locations/EditLocationDetails'
import EditLocationAddress from './Locations/EditLocationAddress'
import LocationAppAccess from './Locations/LocationAppAccess'
import { mapActions, mapGetters } from 'vuex'

/**
 * Locations module is a page for the locations dashboard and creation process.
 * @class Locations
 * @since 0.0.8
 */

export default {
	data () {
		return {
			breadcrumbArray: [
				{name: 'My Business', link: false},
				{name: 'Locations', link: false}
			],
			activePanel: true,
			computedHeight: 0,
			locations: [],
			searchCollapse: true,
			searchError: '',
			advancedSearch: {
				search: '',
				searchOptions: [{name: 'Location Name', value: 'name', checked: true}]
			},
			sortBy: {
				field: 'name',
				order: 'asc'
			},
			sortOptions: {
				name: 'Location Name'
			},
			pageResultsValue: 0,
			numPages: 0,
			totalResults: 0,
			activePage: 1,
			loadingLocationData: false
		}
	},
	/**
	 * Run on `created` to initialize the throttle events and to check the current process step.
	 * @function
	 * @returns {undefined}
	 * @memberof Roles
	 * @version 0.0.8
	 */
	created () {
		this.throttledCreate = throttle(() => {
			this.createLocation()
		}, 500, { 'trailing': false })

		if (this.$route.name !== 'My Business Locations') {
			this.activePanel = false
		}

		let _this = this

		this.computeHeight()

		$(window).on('resize', debounce(() => {
			_this.computeHeight()
		}, 200))
	},
	computed: {
		/**
		 * Compute active search options for use in displaying in sentence format.
		 * @function
		 * @returns {array} - An array of active search option objects.
		 * @memberof Locations
		 * @version 0.0.8
		 */
		computedSearchOptions () {
			return this.advancedSearch.searchOptions.filter(option => {
				return option.checked
			})
		},
		...mapGetters([
			'activeBusinessId'
		])
	},
	methods: {
		/**
		 * To compute height to display based on window height and navbar
		 * @function
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		computeHeight () {
			const height = $(window).height() - 50
			this.computedHeight = height + 'px'
		},
		/**
		 * To close anything active in the side panel by redirecting to the location dashboard route
		 * @function
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		closePanel () {
			this.$router.push('/app/my_business/locations')
		},
		/**
		 * To initialize the create location process by redirecting to the create location route
		 * @function
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		createLocation () {
			this.$router.push('/app/my_business/locations/create_location')
		},
		/**
		 * To toggle the search panel, initially set to closed
		 * @function
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		toggleSearchPanel () {
			this.searchCollapse = !this.searchCollapse
		},
		/**
		 * To send the advanced search options to the backend to fetch locations.
		 * @function
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		advancedLocationSearch () {
			this.clearSearchError()
			if (this.advancedSearch.search.length && this.advancedSearch.search.length < 3) {
				this.searchError = 'In order to use the search field your term must be at least 3 characters.'
			} else if (this.advancedSearch.search.length && !this.computedSearchOptions.length) {
				this.searchError = 'In order to use the search field you must select at least one field to search by.'
			} else {
				if (parseInt(this.activePage) === 1) {
					this.getLocations(1)
				} else {
					this.activePageUpdate(1)
				}
			}
		},
		/**
		 * To clear the current search error.
		 * @function
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		clearSearchError () {
			this.searchError = ''
		},
		/**
		 * To clear the current location search criteria.
		 * @function
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		resetLocationSearch () {
			this.advancedSearch = {
				search: '',
				searchOptions: [{name: 'Location Name', value: 'location_name', checked: true}]
			}
			this.clearSearchError()
		},
		/**
		 * To catch updates from the PageResults component when the number of page results is updated.
		 * @function
		 * @param {integer} val - The number of page results to be returned.
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		pageResultsUpdate (val) {
			if (parseInt(this.pageResultsValue) !== parseInt(val)) {
				this.pageResultsValue = val
				if (parseInt(this.activePage) === 1) {
					this.getLocations(1)
				} else {
					this.activePageUpdate(1)
				}
			}
		},
		/**
		 * To update the currently active pagination page.
		 * @function
		 * @param {integer} val - An integer representing the page number that we are updating to.
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		activePageUpdate (val) {
			if (parseInt(this.activePage) !== parseInt(val)) {
				this.activePage = val
				this.getLocations(this.activePage)
			}
		},
		/**
		 * To update the sort by field and make a backend call to pull the sorted data.
		 * @function
		 * @param {string} field - The field that the user wants to sort by.
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		setSortField (field) {
			if (field !== this.sortBy.field) {
				this.sortBy.field = field
				if (parseInt(this.activePage) === 1) {
					this.getLocations(1)
				} else {
					this.activePageUpdate(1)
				}
			}
		},
		/**
		 * To format the search criteria for use in the api call
		 * @function
		 * @returns {object} - An object of the formated search data
		 * @memberof Locations
		 * @version 0.0.8
		 */
		formatSearchCriteria () {
			let searchCriteria = {}

			if (this.advancedSearch.search.length > 2 && this.computedSearchOptions.length) {
				searchCriteria.search_query = this.advancedSearch.search
				let searchIn = ''
				for (let key in this.computedSearchOptions) {
					if (parseInt(key) !== 0) {
						searchIn += ','
					}
					searchIn += this.computedSearchOptions[key].value
				}
				searchCriteria.search_in = searchIn
			}

			return searchCriteria
		},
		/**
		 * To get a list of locations for the current page.
		 * @function
		 * @param {integer} pageNumber - The current page that we are retrieving results for.
		 * @returns {object} - A promise that will either return an error message or perform an action.
		 * @memberof Locations
		 * @version 0.0.8
		 * @todo Remove timeout
		 */
		getLocations (pageNumber) {
			this.loadingLocationData = true
			this.locations = []

			let _this = this
			const searchCriteria = this.formatSearchCriteria()
			const sortCriteria = {sort_by: this.sortBy.field, sort_order: this.sortBy.order}
			const businessId = this.activeBusinessId

			return Location.getLocations(businessId, pageNumber, this.pageResultsValue, searchCriteria, sortCriteria).then(response => {
				_this.locations = response.payload.locations
				_this.numPages = response.payload.number_of_pages
				_this.totalResults = response.payload.number_of_records
				// Timeout is temporary until backend data is real
				setTimeout(() => { _this.loadingLocationData = false }, 500)
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'no_locations_found':
						_this.numPages = 0
						_this.totalResults = 0
						break

					case 'business_not_found':
						_this.numPages = 0
						_this.totalResults = 0
						_this.$swal({
							title: 'Error',
							html: 'There is an issue with your business account. Please contact your admin.',
							type: 'warning'
						})
						break

					case 'invalid_business':
						_this.numPages = 0
						_this.totalResults = 0
						_this.$swal({
							title: 'Error',
							html: 'There is an issue with your business account. Please contact your admin.',
							type: 'warning'
						})
						break

					case 'invalid_page_number':
						_this.numPages = e.responseJSON.payload.number_of_pages
						_this.totalResults = e.responseJSON.payload.number_of_records
						if (_this.numPages !== 0) {
							_this.getLocations(e.responseJSON.payload.number_of_pages)
						}
						break
					default:
						// No default
					}
				}
				_this.loadingLocationData = false
			}))
		},
		/**
		 * To set the selected location as the active location.
		 * @function
		 * @param {object} location - The location to set as active
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.9
		 */
		selectLocation (location) {
			this.setActiveLocationId(location.location_id)
			this.setActiveLocationName(location.location_name)

			// store the location in localStorage
			/* eslint-disable no-undef */
			localStorage.setItem('activeLocation', JSON.stringify(location))
		},
		/**
		 * To initialize the edit location detials process by redirecting to the edit location details route
		 * @function
		 * @param {integer} locationId - The id of the location that we are editing.
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		editLocationDetails (locationId) {
			this.$router.push('/app/my_business/locations/edit_location_details/' + locationId)
		},
		/**
		 * To initialize the edit location address process by redirecting to the edit location address route
		 * @function
		 * @param {integer} locationId - The id of the location that we are editing.
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		editLocationAddress (locationId) {
			this.$router.push('/app/my_business/locations/edit_location_address/' + locationId)
		},
		/**
		 * To initialize the location app access process by redirecting to the location app access route
		 * @function
		 * @param {integer} locationId - The id of the location that we are editing.
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		editLocationAppAccess (locationId) {
			this.$router.push('/app/my_business/locations/location_app_access/' + locationId)
		},
		...mapActions([
			'setActiveLocationId',
			'setActiveLocationName'
		])
	},
	components: {
		Breadcrumb,
		Dropdown,
		LoadingScreen,
		NoResults,
		PageResults,
		Pagination,
		CreateLocation,
		EditLocationDetails,
		EditLocationAddress,
		LocationAppAccess
	},
	watch: {
		/**
		 * Watch `$route` so that we can check if we are on the proper panel after each change.
		 * @function
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		'$route' () {
			if (this.$route.name !== 'My Business Locations') {
				this.activePanel = false
			} else {
				this.activePanel = true
				this.getLocations(this.activePage)
			}
		},
		/**
		 * Watch `sortBy.order` so that we can update the active page and retrieve the locations with the updated sort order.
		 * @function
		 * @returns {undefined}
		 * @memberof Locations
		 * @version 0.0.8
		 */
		'sortBy.order' () {
			if (this.sortBy.field) {
				if (parseInt(this.activePage) === 1) {
					this.getLocations(1)
				} else {
					this.activePageUpdate(1)
				}
			}
		}
	}
}
</script>


// WEBPACK FOOTER //
// Locations.vue?1df323e6